import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, concatMap, map, of, startWith, switchMap, tap } from 'rxjs';

import {
  DataVisuFactory,
  collectOccupationSiteUids
} from '@gfe/core/models/data-visualization/data-visualization.state';
import {
  EntityCfg,
  EntityInterface
} from '@gfe/core/models/entities/entity-visualization-interface';
import { PlanFilterOperator } from '@gfe/core/models/entity/entity-selector-request.model';
import {
  AuxiliaryDataContext,
  AuxiliaryDataService
} from '@gfe/core/services/auxiliary-data/auxiliary-data.service';
import { EntityHydrationService } from '@gfe/core/services/entity-hydration/entity-hydration.service';
import { OccupationDataService } from '@gfe/core/services/occupation-data/occupation-data.service';
import { GenericEntityRegistryService } from '@gfe/core/services/user-models-registry/generic-entity-registry.service';
import { UserModelsRegistryService } from '@gfe/core/services/user-models-registry/user-models-registry.service';
import { isNil } from '@gfe/core/utils/shared-utils';
import { CalendarModificationActionsService } from '@gfe/features/components/visualization/calendar/calendar-modification-actions.service';
import { GeoAreaModificationActionsService } from '@gfe/features/components/visualization/mapbox/geo-area-modification-actions.service';
import { TimelineModificationActionsService } from '@gfe/features/components/visualization/timeline/timeline-modification-actions.service';

import {
  selectAggregatorFilter,
  selectOccupationVisualization,
  selectVisualizationContext
} from '@core/store/data-visualization/data-visualization.selectors';
import { selectPlanWatchForView } from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanEditActions } from '@core/store/plan-edit/plan-edit.actions';
import { PlanWatchState, watchShowsLive } from '@core/store/plan-edit/plan-edit.state';
import { overlaidRaw, overlayAuxBundle } from '@core/store/plan-edit/plan-watch-overlay';

import * as VisuActions from './data-visualization.actions';
import { setFilterOnAggregator } from './data-visualization.actions';

/** The watch applies to the watched plan's own rows only; the import picker reads the others. */
function watchFor(
  watch: PlanWatchState,
  auxContext?: AuxiliaryDataContext
): PlanWatchState | null {
  return watch.planId !== null &&
    auxContext?.planId === watch.planId &&
    auxContext.planOperator === PlanFilterOperator.IN
    ? watch
    : null;
}

@Injectable()
export class VisualizationEffects {
  private actions$ = inject(Actions);

  private readonly store = inject(Store);
  private readonly timelineModActions = inject(TimelineModificationActionsService);
  private readonly calendarModActions = inject(CalendarModificationActionsService);
  private readonly areaModActions = inject(GeoAreaModificationActionsService);
  private readonly auxiliaryData = inject(AuxiliaryDataService);
  private readonly hydration = inject(EntityHydrationService);
  private readonly occupationData = inject(OccupationDataService);
  private readonly userModels = inject(UserModelsRegistryService);
  private readonly registry = inject(GenericEntityRegistryService);

  onSetVisualizedEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisuActions.setVisualizedEntities),
      concatLatestFrom(() => [
        this.store.select(selectAggregatorFilter),
        this.store.select(selectPlanWatchForView)
      ]),
      switchMap(([{ entityCfg, entities, auxContext }, activeFilters, watch]) => {
        let isFilteringOnMissingData = false;
        const removeFilteredIds: string[] = [];
        if (activeFilters.filteredAggregatorIds.length > 0) {
          activeFilters.filteredAggregatorIds.map((id) => {
            if (!entities.find((ent) => ent.uid === id)) {
              isFilteringOnMissingData = true;
              removeFilteredIds.push(id);
            }
          });
        }

        if (isFilteringOnMissingData) {
          this.store.dispatch(
            setFilterOnAggregator({
              filterMethod: activeFilters.filterMethod,
              filteredAggregatorIds: activeFilters.filteredAggregatorIds.filter(
                (id) => !removeFilteredIds.includes(id)
              )
            })
          );
        }

        const built$ = this.prepareForVisualization$(
          entityCfg,
          entities,
          auxContext,
          watchFor(watch, auxContext)
        ).pipe(
          concatMap((visualizedEntities) => {
            const {
              states: newState,
              timelineModificationActionsMap,
              calendarModificationsMap,
              areaModificationsMap
            } = DataVisuFactory.buildVisuComponentStates(entityCfg, visualizedEntities);

            this.timelineModActions.setActions(timelineModificationActionsMap);
            this.calendarModActions.setModifications(calendarModificationsMap);
            this.areaModActions.setModifications(areaModificationsMap);

            return [
              VisuActions.setVisuComponentStates({ states: newState }),
              // After hydration, never before: a summary route row carries no `actions`, so
              // the sites it stops at cannot be read off it.
              VisuActions.setOccupationSitesInView({
                siteUids: collectOccupationSiteUids(visualizedEntities)
              }),
              VisuActions.setTimelineModificationDescriptors({
                descriptors: this.timelineModActions.buildDescriptors()
              })
            ];
          })
        );

        // Announce the wait only when there is one. Emitting it unconditionally would flash a
        // spinner on every selection change for entities that need no extra fetch.
        return this.needsHydration(entityCfg, entities, auxContext)
          ? built$.pipe(
              startWith(VisuActions.setVisualizationHydrating({ hydrating: true }))
            )
          : built$;
      })
    )
  );

  /** A live edit's save rewrites the plan's rows in place, just like a direct write. */
  onEntityModified$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VisuActions.entityModified, PlanEditActions.planDataReplaced),
        tap(() => {
          this.auxiliaryData.invalidate();
          this.hydration.invalidate();
          this.occupationData.invalidate();
        })
      ),
    { dispatch: false }
  );

  /**
   * Resolves the occupation at every site asked for so far, whenever a new one is added.
   *
   * `switchMap` rather than `concatMap`: each request supersedes the last, because the
   * accumulated site set only grows and the newest fetch already covers the older one.
   */
  onOccupationRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisuActions.occupationRequested),
      concatLatestFrom(() => [
        this.store.select(selectOccupationVisualization),
        this.store.select(selectVisualizationContext)
      ]),
      switchMap(([, occupation, context]) => {
        if (isNil(context) || occupation.requestedSiteUids.length === 0) {
          return of(VisuActions.setOccupationSeries({ series: [] }));
        }
        return this.occupationData.seriesFor$(occupation.requestedSiteUids, context).pipe(
          map((series) => VisuActions.setOccupationSeries({ series })),
          startWith(VisuActions.setOccupationLoading({ loading: true }))
        );
      })
    )
  );

  /**
   * Turns the rows the table selected into entities a visualization can be built from: the
   * complete payload first, then the related entities its builders read.
   *
   * The order matters. A summary row has no `actions`, and the auxiliary uid paths reach into
   * them (`actions[*].site_uid`), so resolving auxiliary data before hydrating would find
   * nothing to resolve. An entity the live edit session holds is taken whole from its overlay
   * instead, and the overlay is laid over the resolved bundle, for the flavours it projects.
   */
  private prepareForVisualization$(
    entityCfg: EntityCfg<unknown>,
    entities: EntityInterface<unknown>[],
    auxContext: AuxiliaryDataContext | undefined,
    watch: PlanWatchState | null
  ): Observable<EntityInterface<unknown>[]> {
    if (entities.length === 0 || isNil(auxContext)) {
      return of(entities);
    }
    const versionName = entityCfg.versionName();
    const held =
      watch === null || !watchShowsLive(watch, versionName)
        ? entities.map(() => undefined)
        : entities.map((e) => overlaidRaw(watch.overlay, versionName, e.uid));
    return this.hydrated$(entityCfg, entities, auxContext, held).pipe(
      switchMap((raws) =>
        this.auxiliaryData
          .resolve$(entityCfg.makeAuxDataFetchDetails(raws), auxContext)
          .pipe(
            map((auxData) =>
              this.userModels.convertToEntityInterface(
                raws,
                versionName,
                undefined,
                watch === null
                  ? auxData
                  : overlayAuxBundle(
                      auxData,
                      watch.overlay,
                      (v) => watchShowsLive(watch, v),
                      (v) => this.registry.entityTypeFor(v)
                    )
              )
            )
          )
      )
    );
  }

  /** Whether the selection will be fetched again — the one case worth showing a wait for. */
  private needsHydration(
    entityCfg: EntityCfg<unknown>,
    entities: EntityInterface<unknown>[],
    auxContext?: AuxiliaryDataContext
  ): boolean {
    return (
      entities.length > 0 &&
      !isNil(auxContext) &&
      (entityCfg.lazyFields?.() ?? []).length > 0 &&
      !isNil(this.registry.entityTypeFor(entityCfg.versionName()))
    );
  }

  /**
   * The raw payloads to visualize. Only entities whose schema declares lazy fields are fetched
   * again — everything else is already complete on the row the table holds. A uid the backend
   * does not return keeps its summary row rather than dropping out of the view.
   *
   * `held` is the session's whole entity for each one it holds. Those are never fetched: the
   * stored row would be replaced anyway, and a route the session opened has none, so the fetch
   * would hold up every later rebuild while it comes back empty.
   */
  private hydrated$(
    entityCfg: EntityCfg<unknown>,
    entities: EntityInterface<unknown>[],
    auxContext: AuxiliaryDataContext,
    held: (Record<string, unknown> | undefined)[]
  ): Observable<unknown[]> {
    const raws = entities.map((e, i) => held[i] ?? e.raw);
    const versionName = entityCfg.versionName();
    const entityType = this.registry.entityTypeFor(versionName);
    const missing = entities.filter((_, i) => held[i] === undefined).map((e) => e.uid);
    if (
      (entityCfg.lazyFields?.() ?? []).length === 0 ||
      isNil(entityType) ||
      missing.length === 0
    ) {
      return of(raws);
    }
    return this.hydration
      .hydrate$(entityType, versionName, missing, auxContext)
      .pipe(map((byUid) => entities.map((e, i) => held[i] ?? byUid.get(e.uid) ?? e.raw)));
  }
}
